import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, CreditCard, Leaf, Users, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [latestProducts, setLatestProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API_URL}/seed`).catch(() => { });

      const [catRes, featuredRes, latestRes, blogsRes] = await Promise.all([
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/products?featured=true&limit=8`),
        axios.get(`${API_URL}/products?limit=8`),
        axios.get(`${API_URL}/blogs?limit=3`)
      ]);

      setCategories(catRes.data);
      setFeaturedProducts(featuredRes.data);
      setLatestProducts(latestRes.data);
      setBlogs(blogsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const howItWorks = [
    { icon: Leaf, title: 'Shop Online', desc: 'Browse our organic products' },
    { icon: Truck, title: 'Fast Delivery', desc: 'Free delivery above ₹500' },
    { icon: CreditCard, title: 'Pay on Delivery', desc: 'Cash on delivery available' },
    { icon: Users, title: 'Farm Fresh', desc: 'Direct from local farmers' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent loading-spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-background overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                100% Organic & Natural
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-stone-900 leading-tight">
                Pure Organic Food From{' '}
                <span className="text-primary">Local Farmers</span>
              </h1>
              <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-xl">
                Experience the authentic taste of traditional Indian organic food.
                Cold pressed oils, millets, desi ghee, and more - sourced directly from
                trusted farmers across India.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/products">
                  <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg" data-testid="shop-now-btn">
                    Shop Now <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-2 border-primary text-primary hover:bg-primary/5">
                    Our Story
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="mt-12 grid grid-cols-3 gap-8">
                <div>
                  <p className="text-3xl font-serif font-semibold text-primary">10+</p>
                  <p className="text-sm text-muted-foreground">Years of Trust</p>
                </div>
                <div>
                  <p className="text-3xl font-serif font-semibold text-primary">15K+</p>
                  <p className="text-sm text-muted-foreground">Happy Families</p>
                </div>
                <div>
                  <p className="text-3xl font-serif font-semibold text-primary">4K+</p>
                  <p className="text-sm text-muted-foreground">Farmer Network</p>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-up delay-200">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_vaidya-connect-2/artifacts/qdxtjijh_image.png"
                  alt="Organic Products"
                  className="w-full h-auto rounded-3xl shadow-2xl"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Leaf className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">Certified </p>
                    <p className="text-sm text-muted-foreground">FSSAI Approved</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900">
                Shop by Category
              </h2>
              <p className="mt-2 text-muted-foreground">
                Explore our wide range of organic products
              </p>
            </div>
            <Link to="/products" className="hidden sm:flex items-center text-primary font-medium hover:underline">
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {Array.isArray(categories) && categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="category-card group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-stone-100 shadow-sm">
                  <img
                    src={category.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-semibold text-white text-lg">{category.name}</h3>
                    <p className="text-white/80 text-sm">{category.product_count} Products</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900">
                Featured Products
              </h2>
              <p className="mt-2 text-muted-foreground">
                Our most popular organic products
              </p>
            </div>
            <Link to="/products?featured=true" className="hidden sm:flex items-center text-primary font-medium hover:underline">
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.isArray(featuredProducts) && featuredProducts.slice(0, 8).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Banner */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-white">
                Why Choose SriSuktam Organics?
              </h2>
              <p className="mt-4 text-white/80 text-lg leading-relaxed">
                We bring you the purest form of organic food, sourced directly from
                local farmers who use traditional farming methods. No chemicals,
                no preservatives - just pure, natural goodness.
              </p>
              <ul className="mt-8 space-y-4">
                {['Direct from farmers - Fair prices', 'Traditional processing methods', 'FSSAI certified', 'PAN India delivery'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white">
                    <span className="h-6 w-6 rounded-full bg-accent flex items-center justify-center text-sm font-semibold text-stone-900">
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="/why_us.png"
                alt="Organic Farm"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Latest Products */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900">
                Latest Arrivals
              </h2>
              <p className="mt-2 text-muted-foreground">
                Fresh additions to our organic collection
              </p>
            </div>
            <Link to="/products" className="hidden sm:flex items-center text-primary font-medium hover:underline">
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.isArray(latestProducts) && latestProducts.slice(0, 8).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900">
              How It Works
            </h2>
            <p className="mt-2 text-muted-foreground">
              Simple steps to get organic food at your doorstep
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="text-center">
                <div className="h-16 w-16 mx-auto bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                  <item.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {blogs.length > 0 && (
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900">
                  From Our Blog
                </h2>
                <p className="mt-2 text-muted-foreground">
                  Tips, recipes, and health insights
                </p>
              </div>
              <Link to="/blog" className="hidden sm:flex items-center text-primary font-medium hover:underline">
                View All <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.isArray(blogs) && blogs.map((blog) => (
                <Link key={blog.id} to={`/blog/${blog.id}`} className="group">
                  <div className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 hover:shadow-lg transition-all duration-300">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={blog.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600'}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex gap-2 mb-3">
                        {blog.tags?.slice(0, 2).map((tag, i) => (
                          <span key={i} className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="font-serif text-xl font-semibold text-stone-900 group-hover:text-primary transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      <p className="mt-2 text-muted-foreground line-clamp-2">
                        {blog.excerpt}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
